import { DeleteOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Col, Input, message, Select } from 'antd';
import TextArea from 'antd/lib/input/TextArea';
import { memo, useCallback } from 'react';
import { ControllerProps, useFormContext } from 'react-hook-form';

import { programsApi } from 'src/features/programsV2/api/programsApi';
import { ContentStepForm } from 'src/features/programsV2/schemas/contentStepSchema';
import {
  genderMapping,
  GenderOptions,
} from 'src/features/programsV2/types/program';
import { AllowedFileTypes } from 'src/features/programsV2/types/programModulesAndContents';
import { allowedDirectoryVideoOptions } from 'src/features/programsV2/utils/formatAllowedDirectoryVideoTypes';
import { formatDetailsOptions } from 'src/features/programsV2/utils/formatDetailsOptions';

import { ProgramFormItem } from '../../ProgramFormItem';
import { TransparentButton } from '../../ui/TransparentButton';
import { AddNewFile } from './AddNewFile';

interface ContentTypeFieldsProps {
  moduleIndex: number;
  contentIndex: number;
  isOpen: boolean;
  selectedGender?: string;
  thumbNames: Record<string, ControllerProps<ContentStepForm>['name']>;
}

const SPALLA_ERROR_MESSAGE =
  'Erro ao buscar banner da spalla, tente novamente removendo e adicionando o id.';

export const ContentTypeFields = memo<ContentTypeFieldsProps>(
  ({ moduleIndex, contentIndex, isOpen, selectedGender, thumbNames }) => {
    const { control, watch, setValue } = useFormContext<ContentStepForm>();

    const contentSelectedName = watch(
      `modules.${moduleIndex}.contents.${contentIndex}.title`
    );

    const contentType = watch(
      `modules.${moduleIndex}.contents.${contentIndex}.type`
    );
    const directoryType = watch(
      `modules.${moduleIndex}.contents.${contentIndex}.directory`
    );

    const { data: instructorsOptions, isLoading: loadingInstructors } =
      useQuery({
        queryKey: ['instructors'],
        queryFn: programsApi.fetchInstructors,
      });

    const getSpallaInfo = useCallback(
      async (spallaId: string) => {
        const hideLoading = message.loading(
          'Buscando imagem padrão da spalla...',
          0
        );
        try {
          const { assets } = await programsApi.fetchSpallaInfo(spallaId);

          if (!assets) {
            message.error(SPALLA_ERROR_MESSAGE);
            return;
          }
          const selectedGenderThumbName =
            genderMapping[
              (selectedGender as GenderOptions) || GenderOptions.Neutral
            ];

          setValue(
            thumbNames[selectedGenderThumbName],
            {
              preview: assets.thumbPreview75,
              file: '',
            },
            { shouldValidate: true }
          );
          message.success(
            `A thumb padrão da spalla para o programa "${contentSelectedName}" foi adicionada com sucesso!`
          );
        } catch (error) {
          message.error(SPALLA_ERROR_MESSAGE);
        } finally {
          hideLoading();
        }
      },
      [selectedGender, setValue, thumbNames]
    );

    if (contentType === AllowedFileTypes.Video) {
      return (
        <>
          <Col span={3}>
            <ProgramFormItem
              control={control}
              name={`modules.${moduleIndex}.contents.${contentIndex}.directory`}
              label='Diretório'
              renderItem={({ field }) => (
                <Select
                  options={allowedDirectoryVideoOptions}
                  onChange={field.onChange}
                  autoFocus={false}
                  value={field.value ? field.value : undefined}
                  placeholder='Selecione o diretório'
                />
              )}
            />
          </Col>

          <Col span={6}>
            <ProgramFormItem
              control={control}
              name={`modules.${moduleIndex}.contents.${contentIndex}.instructorId`}
              label='Treinador'
              renderItem={({ field }) => (
                <Select
                  onChange={field.onChange}
                  value={field.value ? field.value : undefined}
                  autoFocus={false}
                  options={
                    instructorsOptions?.length
                      ? formatDetailsOptions(instructorsOptions)
                      : []
                  }
                  loading={loadingInstructors}
                  placeholder='Selecione o treinador'
                />
              )}
            />
          </Col>

          {!!directoryType && directoryType === 'spalla' && (
            <Col span={6}>
              <ProgramFormItem
                control={control}
                name={`modules.${moduleIndex}.contents.${contentIndex}.file.fileUrl`}
                label='Código ou ID'
                renderItem={({ field }) => (
                  <Input
                    onBlur={() => getSpallaInfo(field.value)}
                    type='text'
                    onChange={field.onChange}
                    value={field.value ? field.value : undefined}
                    autoFocus={false}
                    placeholder='Digite o código ou ID'
                    suffix={
                      <TransparentButton
                        icon={<DeleteOutlined />}
                        style={{
                          width: '14px',
                          height: '22px',
                        }}
                        onClick={() => {
                          field.onChange('');
                        }}
                      />
                    }
                  />
                )}
              />
            </Col>
          )}

          {!!directoryType && directoryType === 'file' && (
            <AddNewFile
              contentType={contentType}
              selectedFileName={`modules.${moduleIndex}.contents.${contentIndex}.file`}
            />
          )}
        </>
      );
    }

    if (contentType === AllowedFileTypes.Link) {
      return (
        <Col span={15}>
          <ProgramFormItem
            control={control}
            name={`modules.${moduleIndex}.contents.${contentIndex}.file.fileUrl`}
            label='Link'
            renderItem={({ field }) => (
              <Input
                onChange={field.onChange}
                autoFocus={false}
                value={field.value ? field.value : undefined}
                placeholder='Selecione o link do conteúdo'
              />
            )}
          />
        </Col>
      );
    }

    if (contentType === AllowedFileTypes.Audio) {
      return (
        <>
          <AddNewFile
            contentType={contentType}
            selectedFileName={`modules.${moduleIndex}.contents.${contentIndex}.file`}
          />
          {!isOpen && (
            <Col span={9}>
              <ProgramFormItem
                control={control}
                name={`modules.${moduleIndex}.contents.${contentIndex}.description`}
                label='Descrição (Opcional)'
                renderItem={({ field }) => (
                  <TextArea
                    onChange={field.onChange}
                    value={field.value ? field.value : undefined}
                    style={{ height: '30px' }}
                    placeholder='Breve descrição sobre o conteúdo'
                  />
                )}
              />
            </Col>
          )}
        </>
      );
    }

    if (contentType) {
      return (
        <AddNewFile
          contentType={contentType}
          selectedFileName={`modules.${moduleIndex}.contents.${contentIndex}.file`}
        />
      );
    }

    return null;
  }
);
